import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  TrendingUp, Users, DollarSign, Clock, BarChart3,
  Activity, Target, ArrowUpRight, ArrowDownRight
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const COLORS = ["#059669", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4", "#ec4899", "#84cc16"];

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card className="card-hover">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium">{title}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {Math.abs(trend)}% vs mes anterior
        </div>
      )}
    </CardContent>
  </Card>
);

const Analytics = () => {
  const [overview, setOverview] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const opts = { headers, withCredentials: true };
      const [ovRes, perfRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, opts),
        axios.get(`${API}/analytics/performance`, opts).catch(() => ({ data: {} })),
      ]);
      setOverview(ovRes.data);
      setPerformance(perfRes.data);
    } catch {
      toast.error("Error al cargar analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !overview) {
    return (
      <Layout title="Analytics">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Analytics Avanzado" subtitle="Métricas ejecutivas, tendencias y KPIs de la organización">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="analytics-tabs">
          <TabsTrigger value="general" data-testid="tab-general"><BarChart3 className="w-4 h-4 mr-2" /> General</TabsTrigger>
          <TabsTrigger value="workforce" data-testid="tab-workforce"><Users className="w-4 h-4 mr-2" /> Fuerza Laboral</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance"><Target className="w-4 h-4 mr-2" /> Desempeño</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-kpis">
            <StatCard title="Total Empleados" value={overview.total_employees} subtitle={`${overview.active_employees} activos`} icon={Users} color="bg-emerald-500" />
            <StatCard title="Tasa de Rotación" value={`${overview.turnover_rate}%`} subtitle="Bajas / Total" icon={TrendingUp} color="bg-amber-500" />
            <StatCard title="Masa Salarial" value={`$${(overview.salary_by_department || []).reduce((a, b) => a + b.total_salary, 0).toLocaleString()}`} subtitle="Total mensual" icon={DollarSign} color="bg-blue-500" />
            <StatCard title="Asistencia Hoy" value={(overview.attendance_trend || []).slice(-1)[0]?.count || 0} subtitle="Registros del día" icon={Clock} color="bg-violet-500" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Attendance Trend */}
            <Card data-testid="attendance-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Activity className="w-4 h-4 text-emerald-600" /> Tendencia de Asistencia (7 días)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={overview.attendance_trend || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={v => v.slice(5)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#059669" strokeWidth={2} dot={{ r: 4 }} name="Asistencias" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Department Distribution */}
            <Card data-testid="dept-distribution-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" /> Distribución por Departamento
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(overview.department_distribution || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={overview.department_distribution} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name}: ${value}`}>
                        {(overview.department_distribution || []).map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[250px] text-slate-400 text-sm">Sin datos de departamentos</div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Leave Distribution */}
          <Card data-testid="leave-distribution-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Solicitudes de Permiso por Tipo</CardTitle>
            </CardHeader>
            <CardContent>
              {(overview.leave_distribution || []).length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={overview.leave_distribution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Solicitudes" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-slate-400 text-sm">Sin datos de permisos</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="workforce">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Hiring Trend */}
            <Card data-testid="hiring-trend-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Tendencia de Contrataciones</CardTitle>
              </CardHeader>
              <CardContent>
                {(overview.hiring_trend || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={overview.hiring_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="hires" fill="#059669" radius={[4, 4, 0, 0]} name="Contrataciones" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">Sin datos de contrataciones</div>
                )}
              </CardContent>
            </Card>

            {/* Salary by Department */}
            <Card data-testid="salary-dept-chart">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Salario Promedio por Departamento</CardTitle>
              </CardHeader>
              <CardContent>
                {(overview.salary_by_department || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={overview.salary_by_department} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                      <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={v => `$${v.toLocaleString()}`} />
                      <Bar dataKey="avg_salary" fill="#3b82f6" radius={[0, 4, 4, 0]} name="Salario Promedio" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">Sin datos salariales</div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="performance">
          {performance && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="performance-kpis">
                <StatCard title="Evaluaciones" value={performance.total_evaluations} subtitle={`${performance.completed_evaluations} completadas`} icon={Target} color="bg-violet-500" />
                <StatCard title="Tasa Capacitación" value={`${performance.training_completion_rate}%`} subtitle={`${performance.training_completed}/${performance.training_total}`} icon={TrendingUp} color="bg-emerald-500" />
                <StatCard title="Tickets Abiertos" value={performance.open_tickets} subtitle={`${performance.resolved_tickets} resueltos`} icon={Activity} color="bg-amber-500" />
                <StatCard title="Total Tickets" value={performance.total_tickets} icon={BarChart3} color="bg-blue-500" />
              </div>
              <Card>
                <CardContent className="p-8 text-center text-slate-500">
                  <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="font-medium">Más métricas de desempeño disponibles</p>
                  <p className="text-sm mt-1">Agrega evaluaciones y capacitaciones para ver más datos</p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Layout>
  );
};

export default Analytics;
